import React, { createContext, useContext, useEffect, useState } from "react";
import { themeConfig, themeVariants } from "../config/theme";

interface ThemeContextType {
  theme: "light" | "dark";
  toggleTheme: () => void;
  colors: typeof themeConfig.colors;
  typography: typeof themeConfig.typography;
  spacing: typeof themeConfig.spacing;
  borderRadius: typeof themeConfig.borderRadius;
  boxShadow: typeof themeConfig.boxShadow;
  animation: typeof themeConfig.animation;
  components: typeof themeConfig.components;
  currentVariant: typeof themeVariants.light;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: "light" | "dark";
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = "light",
}) => {
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    const saved = localStorage.getItem("nari-shakti-theme");
    return (saved as "light" | "dark") || defaultTheme;
  });

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  const currentVariant = themeVariants[theme];

  // Apply CSS custom properties when theme changes
  useEffect(() => {
    const root = document.documentElement;

    // Apply theme variant colors
    root.style.setProperty("--background", currentVariant.background);
    root.style.setProperty("--surface", currentVariant.surface);
    root.style.setProperty("--text-primary", currentVariant.text.primary);
    root.style.setProperty("--text-secondary", currentVariant.text.secondary);
    root.style.setProperty("--text-muted", currentVariant.text.muted);

    // Apply spacing
    Object.entries(themeConfig.spacing).forEach(([key, value]) => {
      root.style.setProperty(`--spacing-${key}`, value);
    });

    // Apply border radius
    Object.entries(themeConfig.borderRadius).forEach(([key, value]) => {
      root.style.setProperty(`--radius-${key}`, value);
    });

    // Apply shadows
    Object.entries(themeConfig.boxShadow).forEach(([key, value]) => {
      root.style.setProperty(`--shadow-${key}`, value);
    });

    // Apply animation
    Object.entries(themeConfig.animation.duration).forEach(([key, value]) => {
      root.style.setProperty(`--duration-${key}`, value);
    });

    // Save theme to localStorage
    localStorage.setItem("nari-shakti-theme", theme);

    // Update data attribute for CSS targeting
    root.setAttribute("data-theme", theme);

    // Hint native form controls/scrollbars
    root.style.colorScheme = theme;
  }, [theme, currentVariant]);

  const value: ThemeContextType = {
    theme,
    toggleTheme,
    colors: themeConfig.colors,
    typography: themeConfig.typography,
    spacing: themeConfig.spacing,
    borderRadius: themeConfig.borderRadius,
    boxShadow: themeConfig.boxShadow,
    animation: themeConfig.animation,
    components: themeConfig.components,
    currentVariant,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
